import type {
  HostedGameSnapshot,
  HostedPartySnapshot,
} from "@cue-the-night/transport";

import {
  HostedPartyClient,
  HostedPartyClientError,
} from "./hosted-party-client";

import {
  gameplayHostAsset,
  gameplayHostName,
  playerAvatarAsset,
  playerRingAsset,
} from "./premium-gameplay-assets";

type HighOrLowPhase =
  | "higher_lower"
  | "red_black"
  | "suit"
  | "lucky_streak"
  | "double_down"
  | "final_bus";

type HighOrLowStep =
  | "SETUP"
  | "PREDICTION"
  | "LOCKED"
  | "OUTCOME"
  | "LUCKY_DECISION"
  | "RISK_DECISION"
  | "REWARD_DISTRIBUTION"
  | "FINAL_CARD"
  | "END_GAME";

type HighOrLowSuit =
  | "hearts"
  | "diamonds"
  | "clubs"
  | "spades";

interface HighOrLowCardView {
  readonly id:
    string;

  readonly rank:
    string;

  readonly suit:
    HighOrLowSuit;

  readonly color:
    "red"
    | "black";
}

interface HighOrLowOutcomeView {
  readonly success:
    boolean;

  readonly label:
    string;

  readonly penalty:
    number;

  readonly reward:
    number;
}

interface HighOrLowRewardView {
  readonly source:
    string;

  readonly total:
    number;
}

interface HighOrLowFinalBusView {
  readonly riderId:
    string;

  readonly predictionIndex:
    number;

  readonly completedPredictions:
    number;

  readonly damage:
    number;

  readonly pattern:
    readonly string[];

  readonly result:
    string | null;
}

interface HighOrLowPublicState {
  readonly phase:
    HighOrLowPhase;

  readonly step:
    HighOrLowStep;

  readonly activePlayerId:
    string;

  readonly turnNumber:
    number;

  readonly currentCard:
    HighOrLowCardView | null;

  readonly lockedPrediction:
    string | null;

  readonly revealedCard:
    HighOrLowCardView | null;

  readonly lastOutcome:
    HighOrLowOutcomeView | null;

  readonly completedPredictions:
    number;

  readonly luckyStreak:
    number;

  readonly riskChoice:
    string | null;

  readonly rewardDistribution:
    HighOrLowRewardView | null;

  readonly finalBus:
    HighOrLowFinalBusView | null;

  readonly winners:
    readonly string[];

  readonly deckRemaining:
    number;
}

export interface HighOrLowPlayerRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;

  readonly participantId:
    string;

  readonly client:
    HostedPartyClient;

  readonly onGameSnapshot:
    (
      snapshot:
        HostedGameSnapshot,
    ) => void;
}

export interface HighOrLowOwnerRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;
}

const AUTO_REVEAL_DELAY_MS =
  650;

const scheduledAutoReveals =
  new Set<string>();

function isRecord(
  value:
    unknown,
): value is
  Record<
    string,
    unknown
  > {
  return (
    typeof value ===
      "object" &&
    value !== null &&
    !Array.isArray(
      value,
    )
  );
}

function escapeHtml(
  value:
    string,
): string {
  const element =
    document.createElement(
      "div",
    );

  element.textContent =
    value;

  return element.innerHTML;
}

function readCard(
  value:
    unknown,
): HighOrLowCardView | null {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (
    !isRecord(value) ||
    typeof value.id !==
      "string" ||
    typeof value.rank !==
      "string" ||
    (
      value.suit !==
        "hearts" &&
      value.suit !==
        "diamonds" &&
      value.suit !==
        "clubs" &&
      value.suit !==
        "spades"
    ) ||
    (
      value.color !==
        "red" &&
      value.color !==
        "black"
    )
  ) {
    throw new Error(
      "High or Low card state is invalid.",
    );
  }

  return {
    id:
      value.id,

    rank:
      value.rank,

    suit:
      value.suit,

    color:
      value.color,
  };
}

function readOutcome(
  value:
    unknown,
): HighOrLowOutcomeView | null {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (
    !isRecord(value) ||
    typeof value.success !==
      "boolean" ||
    typeof value.label !==
      "string" ||
    typeof value.penalty !==
      "number" ||
    typeof value.reward !==
      "number"
  ) {
    throw new Error(
      "High or Low outcome state is invalid.",
    );
  }

  return {
    success:
      value.success,

    label:
      value.label,

    penalty:
      value.penalty,

    reward:
      value.reward,
  };
}

function readReward(
  value:
    unknown,
): HighOrLowRewardView | null {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (
    !isRecord(value) ||
    typeof value.source !==
      "string" ||
    typeof value.total !==
      "number"
  ) {
    throw new Error(
      "High or Low reward state is invalid.",
    );
  }

  return {
    source:
      value.source,

    total:
      value.total,
  };
}

function readFinalBus(
  value:
    unknown,
): HighOrLowFinalBusView | null {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (
    !isRecord(value) ||
    typeof value.riderId !==
      "string" ||
    typeof value.predictionIndex !==
      "number" ||
    typeof value.completedPredictions !==
      "number" ||
    typeof value.damage !==
      "number" ||
    !Array.isArray(
      value.pattern,
    )
  ) {
    throw new Error(
      "High or Low Final Bus state is invalid.",
    );
  }

  return {
    riderId:
      value.riderId,

    predictionIndex:
      value.predictionIndex,

    completedPredictions:
      value.completedPredictions,

    damage:
      value.damage,

    pattern:
      value.pattern
        .filter(
          (
            entry,
          ): entry is string =>
            typeof entry ===
              "string",
        ),

    result:
      typeof value.result ===
        "string"
        ? value.result
        : null,
  };
}

export function readHighOrLowState(
  snapshot:
    HostedGameSnapshot,
): HighOrLowPublicState {
  if (
    snapshot.gameId !==
    "high-or-low"
  ) {
    throw new Error(
      `Unsupported game: ${snapshot.gameId}`,
    );
  }

  const value =
    snapshot.publicState;

  if (
    !isRecord(value) ||
    (
      value.phase !==
        "higher_lower" &&
      value.phase !==
        "red_black" &&
      value.phase !==
        "suit" &&
      value.phase !==
        "lucky_streak" &&
      value.phase !==
        "double_down" &&
      value.phase !==
        "final_bus"
    ) ||
    (
      value.step !==
        "SETUP" &&
      value.step !==
        "PREDICTION" &&
      value.step !==
        "LOCKED" &&
      value.step !==
        "OUTCOME" &&
      value.step !==
        "LUCKY_DECISION" &&
      value.step !==
        "RISK_DECISION" &&
      value.step !==
        "REWARD_DISTRIBUTION" &&
      value.step !==
        "FINAL_CARD" &&
      value.step !==
        "END_GAME"
    ) ||
    typeof value.activePlayerId !==
      "string" ||
    !Number.isInteger(
      value.turnNumber,
    ) ||
    !Number.isInteger(
      value.completedPredictions,
    ) ||
    !Number.isInteger(
      value.luckyStreak,
    ) ||
    !Number.isInteger(
      value.deckRemaining,
    ) ||
    !Array.isArray(
      value.winners,
    )
  ) {
    throw new Error(
      "High or Low public state is invalid.",
    );
  }

  return {
    phase:
      value.phase,

    step:
      value.step,

    activePlayerId:
      value.activePlayerId,

    turnNumber:
      value.turnNumber as
        number,

    currentCard:
      readCard(
        value.currentCard,
      ),

    lockedPrediction:
      typeof value.lockedPrediction ===
        "string"
        ? value.lockedPrediction
        : null,

    revealedCard:
      readCard(
        value.revealedCard,
      ),

    lastOutcome:
      readOutcome(
        value.lastOutcome,
      ),

    completedPredictions:
      value.completedPredictions as
        number,

    luckyStreak:
      value.luckyStreak as
        number,

    riskChoice:
      typeof value.riskChoice ===
        "string"
        ? value.riskChoice
        : null,

    rewardDistribution:
      readReward(
        value.rewardDistribution,
      ),

    finalBus:
      readFinalBus(
        value.finalBus,
      ),

    winners:
      value.winners.filter(
        (
          entry,
        ): entry is string =>
          typeof entry ===
            "string",
      ),

    deckRemaining:
      value.deckRemaining as
        number,
  };
}

function activateVisual(
  phase:
    HighOrLowPhase,
): void {
  const root =
    document.documentElement;

  root.dataset.ctnGame =
    "high-or-low";

  root.dataset.ctnMechanic =
    phase;
}

function playerName(
  party:
    HostedPartySnapshot,
  participantId:
    string,
): string {
  return (
    party.participants.find(
      (participant) =>
        participant.participantId ===
        participantId,
    )?.displayName ??
    "Player"
  );
}

function playerAvatarId(
  party:
    HostedPartySnapshot,
  participantId:
    string,
): string {
  return (
    party.participants.find(
      (participant) =>
        participant.participantId ===
        participantId,
    )?.avatarId ??
    "avatar-001"
  );
}

function phaseLabel(
  phase:
    HighOrLowPhase,
): string {
  const labels:
    Record<
      HighOrLowPhase,
      string
    > = {
      higher_lower:
        "HIGHER OR LOWER",

      red_black:
        "RED OR BLACK",

      suit:
        "CALL THE SUIT",

      lucky_streak:
        "LUCKY STREAK",

      double_down:
        "DOUBLE DOWN",

      final_bus:
        "FINAL BUS",
    };

  return labels[
    phase
  ];
}

function phaseNumber(
  phase:
    HighOrLowPhase,
): number {
  const phases:
    readonly HighOrLowPhase[] = [
      "higher_lower",
      "red_black",
      "suit",
      "lucky_streak",
      "double_down",
      "final_bus",
    ];

  return (
    phases.indexOf(
      phase,
    ) + 1
  );
}

function suitSymbol(
  suit:
    HighOrLowSuit,
): string {
  if (
    suit === "hearts"
  ) {
    return "♥";
  }

  if (
    suit === "diamonds"
  ) {
    return "♦";
  }

  if (
    suit === "clubs"
  ) {
    return "♣";
  }

  return "♠";
}

const HIGH_OR_LOW_CARD_ASSET_BASE =
  "/assets/shared/cards/standard52";

const HIGH_OR_LOW_CARD_BACK_ASSET =
  `${HIGH_OR_LOW_CARD_ASSET_BASE}/card-back.webp`;

function highOrLowCardAsset(
  card:
    HighOrLowCardView,
): string {
  const suit =
    card.suit.slice(
      0,
      -1,
    );

  const rank =
    card.rank === "A"
      ? "ace"
      : card.rank === "K"
        ? "king"
        : card.rank === "Q"
          ? "queen"
          : card.rank === "J"
            ? "jack"
            : card.rank.toLowerCase();

  return (
    `${HIGH_OR_LOW_CARD_ASSET_BASE}/${suit}-${rank}.webp`
  );
}

function cardHtml(
  card:
    HighOrLowCardView | null,
  hidden:
    boolean,
  label:
    string,
): string {
  if (hidden) {
    return `
      <div class="hol-card hol-card-back">
        <img
          class="hol-card-art"
          src="${HIGH_OR_LOW_CARD_BACK_ASSET}"
          alt="Face-down playing card"
        />

        <small>
          ${escapeHtml(label)}
        </small>
      </div>
    `;
  }

  if (!card) {
    return `
      <div class="hol-card hol-card-empty">
        <span>
          —
        </span>

        <small>
          ${escapeHtml(label)}
        </small>
      </div>
    `;
  }

  const symbol =
    suitSymbol(
      card.suit,
    );

  const asset =
    highOrLowCardAsset(
      card,
    );

  return `
    <div
      class="hol-card hol-card-face ${card.color}"
    >
      <img
        class="hol-card-art"
        src="${asset}"
        alt="${escapeHtml(`${card.rank}${symbol}`)}"
      />

      <small>
        ${escapeHtml(label)}
      </small>
    </div>
  `;
}

function predictionActions(
  phase:
    HighOrLowPhase,
): string {
  if (
    phase ===
      "red_black"
  ) {
    return `
      <div class="hol-prediction-grid two">
        <button
          class="hol-action hol-prediction red"
          data-command="predict"
          data-choice="red"
          type="button"
        >
          <span>♥ ♦</span>
          <strong>RED</strong>
        </button>

        <button
          class="hol-action hol-prediction black"
          data-command="predict"
          data-choice="black"
          type="button"
        >
          <span>♠ ♣</span>
          <strong>BLACK</strong>
        </button>
      </div>
    `;
  }

  if (
    phase === "suit"
  ) {
    return `
      <div class="hol-prediction-grid suits">
        <button
          class="hol-action hol-prediction red"
          data-command="predict"
          data-choice="hearts"
          type="button"
        >
          <span>♥</span>
          <strong>HEARTS</strong>
        </button>

        <button
          class="hol-action hol-prediction red"
          data-command="predict"
          data-choice="diamonds"
          type="button"
        >
          <span>♦</span>
          <strong>DIAMONDS</strong>
        </button>

        <button
          class="hol-action hol-prediction black"
          data-command="predict"
          data-choice="clubs"
          type="button"
        >
          <span>♣</span>
          <strong>CLUBS</strong>
        </button>

        <button
          class="hol-action hol-prediction black"
          data-command="predict"
          data-choice="spades"
          type="button"
        >
          <span>♠</span>
          <strong>SPADES</strong>
        </button>
      </div>
    `;
  }

  return `
    <div class="hol-prediction-grid two">
      <button
        class="hol-action hol-prediction higher"
        data-command="predict"
        data-choice="higher"
        type="button"
      >
        <span>↑</span>
        <strong>HIGHER</strong>
      </button>

      <button
        class="hol-action hol-prediction lower"
        data-command="predict"
        data-choice="lower"
        type="button"
      >
        <span>↓</span>
        <strong>LOWER</strong>
      </button>
    </div>
  `;
}

function outcomeHtml(
  state:
    HighOrLowPublicState,
): string {
  const outcome =
    state.lastOutcome;

  if (!outcome) {
    return "";
  }

  const resultClass =
    outcome.success
      ? "success"
      : "failure";

  let detail =
    "";

  if (
    outcome.penalty >
    0
  ) {
    detail =
      `${outcome.penalty} drink${
        outcome.penalty === 1
          ? ""
          : "s"
      }`;
  }

  if (
    outcome.reward >
    0
  ) {
    detail =
      `Give ${outcome.reward}`;
  }

  return `
    <section class="hol-outcome ${resultClass}">
      <span>
        ${
          outcome.success
            ? "NICE CALL"
            : "MISSED IT"
        }
      </span>

      <strong>
        ${escapeHtml(
          outcome.label
            .replaceAll(
              "_",
              " ",
            ),
        )}
      </strong>

      ${
        detail
          ? `
            <small>
              ${escapeHtml(detail)}
            </small>
          `
          : ""
      }
    </section>
  `;
}

function finalBusProgress(
  state:
    HighOrLowPublicState,
): string {
  if (
    state.phase !==
      "final_bus" ||
    !state.finalBus
  ) {
    return "";
  }

  const slots =
    Array.from(
      {
        length: 5,
      },
      (
        _,
        index,
      ) => {
        const result =
          state.finalBus
            ?.pattern[
              index
            ];

        const active =
          index ===
          state.finalBus
            ?.predictionIndex &&
          state.step !==
          "END_GAME";

        return `
          <span
            class="hol-bus-stop ${
              result === "S"
                ? "success"
                : result === "F"
                  ? "failure"
                  : active
                    ? "active"
                    : ""
            }"
          >
            ${
              result === "S"
                ? "✓"
                : result === "F"
                  ? "×"
                  : index + 1
            }
          </span>
        `;
      },
    ).join("");

  return `
    <section class="hol-bus-progress">
      <div>
        <span class="meta">
          FINAL BUS
        </span>

        <strong>
          CARD ${
            Math.min(
              state.finalBus
                .predictionIndex +
              1,
              5,
            )
          } / 5
        </strong>
      </div>

      <div class="hol-bus-stops">
        ${slots}
      </div>

      <span class="hol-damage">
        ${state.finalBus.damage}
        damage
      </span>
    </section>
  `;
}

function actionsHtml(
  options:
    HighOrLowPlayerRenderOptions,
  state:
    HighOrLowPublicState,
): string {
  const isActive =
    state.activePlayerId ===
    options.participantId;

  if (!isActive) {
    return `
      <div class="hol-waiting">
        Waiting for
        <strong>
          ${escapeHtml(
            playerName(
              options.party,
              state.activePlayerId,
            ),
          )}
        </strong>
      </div>
    `;
  }

  if (
    state.step ===
      "PREDICTION" ||
    state.step ===
      "FINAL_CARD"
  ) {
    return predictionActions(
      state.phase,
    );
  }

  if (
    state.step ===
    "LOCKED"
  ) {
    return `
      <div class="hol-waiting hol-auto-reveal">
        <strong>
          CALL LOCKED
        </strong>

        <span>
          Revealing…
        </span>
      </div>
    `;
  }

  if (
    state.step ===
    "LUCKY_DECISION"
  ) {
    return `
      <div class="hol-prediction-grid two">
        <button
          class="hol-action hol-primary"
          data-command="keep-going"
          type="button"
        >
          <span>🔥</span>
          <strong>KEEP GOING</strong>
          <small>
            Protect nothing.
          </small>
        </button>

        <button
          class="hol-action hol-cash"
          data-command="cash-out"
          type="button"
        >
          <span>✓</span>
          <strong>CASH OUT</strong>
          <small>
            Give ${Math.min(
              state.luckyStreak,
              5,
            )}
          </small>
        </button>
      </div>
    `;
  }

  if (
    state.step ===
    "RISK_DECISION"
  ) {
    return `
      <div class="hol-prediction-grid two">
        <button
          class="hol-action hol-safe"
          data-command="risk"
          data-risk="safe"
          type="button"
        >
          <span>🛡</span>
          <strong>PLAY IT SAFE</strong>
        </button>

        <button
          class="hol-action hol-double"
          data-command="risk"
          data-risk="double"
          type="button"
        >
          <span>×2</span>
          <strong>DOUBLE DOWN</strong>
        </button>
      </div>
    `;
  }

  if (
    state.step ===
      "REWARD_DISTRIBUTION" &&
    state.rewardDistribution
  ) {
    const targets =
      options.party
        .participants
        .filter(
          (participant) =>
            participant.status ===
              "ACTIVE" &&
            participant.participantId !==
              state.activePlayerId,
        )
        .map(
          (participant) => `
            <button
              class="hol-action hol-target"
              data-command="reward"
              data-target="${escapeHtml(participant.participantId)}"
              type="button"
            >
              <strong>
                ${escapeHtml(participant.displayName)}
              </strong>

              <small>
                Give all ${state.rewardDistribution?.total ?? 0}
              </small>
            </button>
          `,
        )
        .join("");

    return `
      <section class="hol-reward-panel">
        <p class="eyebrow">
          YOU EARNED CONTROL
        </p>

        <strong>
          Give ${
            state.rewardDistribution.total
          }
        </strong>

        <div class="hol-target-grid">
          ${targets}
        </div>
      </section>
    `;
  }

  if (
    state.step ===
    "OUTCOME"
  ) {
    return `
      <button
        class="hol-action hol-primary"
        data-command="continue"
        type="button"
      >
        CONTINUE
      </button>
    `;
  }

  if (
    state.step ===
    "END_GAME"
  ) {
    return `
      <div class="hol-waiting">
        Final Bus complete.
        Waiting for the host.
      </div>
    `;
  }

  return "";
}

export function renderPlayerHighOrLow(
  options:
    HighOrLowPlayerRenderOptions,
): void {
  const state =
    readHighOrLowState(
      options.game,
    );

  activateVisual(
    state.phase,
  );

  const activeName =
    playerName(
      options.party,
      state.activePlayerId,
    );

  const isActive =
    state.activePlayerId ===
    options.participantId;

  const activeAvatar =
    playerAvatarAsset(
      playerAvatarId(
        options.party,
        state.activePlayerId,
      ),
    );

  const revealed =
    state.revealedCard;

  const showHiddenTarget =
    (
      state.step ===
        "PREDICTION" ||
      state.step ===
        "LOCKED" ||
      state.step ===
        "FINAL_CARD"
    );

  const showTwoCards =
    state.currentCard !==
      null;

  const hostName =
    gameplayHostName(
      options.party
        .activeVirtualHostId,
    );

  const hostAsset =
    gameplayHostAsset(
      options.party
        .activeVirtualHostId,
      state.step ===
        "END_GAME"
        ? "signoff"
        : state.step ===
            "OUTCOME"
          ? "reveal"
          : state.step ===
              "LOCKED"
            ? "suspense"
            : "presenting",
    );

  const winners =
    state.winners
      .map(
        (id) =>
          playerName(
            options.party,
            id,
          ),
      );

  options.panel.innerHTML = `
    <div class="game-screen hol-screen">
      <div class="game-topline">
        <p class="eyebrow">
          HIGH OR LOW
        </p>

        <span class="game-turn">
          PHASE ${
            phaseNumber(
              state.phase,
            )
          } / 6
        </span>
      </div>

      <section
        class="premium-turn-hero hol-turn-hero ${
          isActive
            ? "is-active-player"
            : "is-waiting-player"
        }"
      >
        <div class="premium-player-identity">
          <div class="premium-avatar-orbit">
            <div class="premium-avatar-shell">
              <img
                class="premium-avatar-image"
                src="${activeAvatar}"
                alt="${escapeHtml(activeName)}"
              />

              <img
                class="premium-avatar-ring-art"
                src="${playerRingAsset}"
                alt=""
                aria-hidden="true"
              />
            </div>
          </div>

          <div class="premium-player-copy">
            <span class="premium-turn-kicker">
              ${
                isActive
                  ? "YOUR TURN"
                  : "NOW PLAYING"
              }
            </span>

            <strong class="premium-player-name">
              ${escapeHtml(activeName)}
            </strong>

            <span class="premium-player-context">
              ${phaseLabel(state.phase)}
            </span>
          </div>
        </div>

        <aside class="rex-gameplay-presence">
          <img
            src="${hostAsset}"
            alt="${escapeHtml(hostName)}"
          />

          <div class="rex-presence-label">
            <span>HOST</span>
            <strong>
              ${escapeHtml(hostName.toUpperCase())}
            </strong>
          </div>
        </aside>
      </section>

      <section class="hol-phase-banner">
        <div>
          <span>
            PHASE ${
              phaseNumber(
                state.phase,
              )
            }
          </span>

          <strong>
            ${phaseLabel(state.phase)}
          </strong>
        </div>

        ${
          state.phase ===
            "lucky_streak"
            ? `
              <div class="hol-streak">
                🔥 ${state.luckyStreak}
              </div>
            `
            : ""
        }

        ${
          state.phase ===
            "double_down" &&
          state.riskChoice
            ? `
              <div class="hol-risk">
                ${
                  state.riskChoice ===
                    "double"
                    ? "×2 DOUBLE"
                    : "SAFE"
                }
              </div>
            `
            : ""
        }
      </section>

      ${finalBusProgress(state)}

      <section
        class="hol-table ${
          showTwoCards
            ? "two-card"
            : "one-card"
        }"
      >
        ${
          showTwoCards
            ? cardHtml(
                state.currentCard,
                false,
                state.phase ===
                  "final_bus"
                  ? "CURRENT"
                  : "BASE CARD",
              )
            : ""
        }

        ${
          revealed
            ? cardHtml(
                revealed,
                false,
                "REVEALED",
              )
            : cardHtml(
                null,
                showHiddenTarget,
                showHiddenTarget
                  ? "NEXT CARD"
                  : "WAITING",
              )
        }
      </section>

      ${
        state.lockedPrediction
          ? `
            <div class="hol-locked-call">
              <span>
                LOCKED CALL
              </span>

              <strong>
                ${escapeHtml(
                  state.lockedPrediction
                    .replaceAll(
                      "_",
                      " ",
                    )
                    .toUpperCase(),
                )}
              </strong>
            </div>
          `
          : ""
      }

      ${outcomeHtml(state)}

      ${
        state.step ===
          "END_GAME"
          ? `
            <section class="hol-final-result">
              <p class="eyebrow">
                NIGHT RESULT
              </p>

              <strong>
                ${
                  state.finalBus?.result
                    ? escapeHtml(
                        state.finalBus.result
                          .replaceAll(
                            "_",
                            " ",
                          ),
                      )
                    : "FINAL BUS COMPLETE"
                }
              </strong>

              ${
                winners.length > 0
                  ? `
                    <span>
                      Winner${
                        winners.length === 1
                          ? ""
                          : "s"
                      }:
                      ${escapeHtml(
                        winners.join(
                          ", ",
                        ),
                      )}
                    </span>
                  `
                  : ""
              }
            </section>
          `
          : ""
      }

      <section class="hol-actions">
        ${actionsHtml(
          options,
          state,
        )}
      </section>

      <div class="hol-meta-row">
        <span>
          ${state.completedPredictions}
          calls
        </span>

        <span>
          ${state.deckRemaining}
          cards left
        </span>

        <span>
          TURN ${state.turnNumber}
        </span>
      </div>

      <p
        class="status game-status"
        id="hol-status"
      ></p>
    </div>
  `;

  const buttons =
    Array.from(
      options.panel
        .querySelectorAll(
          ".hol-action",
        ),
    ).filter(
      (
        element,
      ): element is HTMLButtonElement =>
        element instanceof
        HTMLButtonElement,
    );

  const status =
    options.panel
      .querySelector(
        "#hol-status",
      );

  async function execute(
    command:
      Readonly<
        Record<
          string,
          unknown
        >
      >,
  ): Promise<void> {
    for (
      const button of
      buttons
    ) {
      button.disabled =
        true;
    }

    if (
      status instanceof
      HTMLElement
    ) {
      status.textContent =
        "Dealing…";
    }

    try {
      const result =
        await options.client
          .sendGameCommand(
            options.game
              .gameSessionId,
            options.game
              .stateVersion,
            command,
          );

      options.onGameSnapshot(
        result.snapshot,
      );
    } catch (error) {
      for (
        const button of
        buttons
      ) {
        button.disabled =
          false;
      }

      if (
        status instanceof
        HTMLElement
      ) {
        status.textContent =
          error instanceof
            HostedPartyClientError
            ? error.message
            : error instanceof Error
              ? error.message
              : "Unable to update High or Low.";
      }
    }
  }

  function scheduleAutoReveal():
  void {
    if (
      !isActive ||
      state.step !==
        "LOCKED"
    ) {
      return;
    }

    const key =
      `${options.game.gameSessionId}:${options.game.stateVersion}`;

    if (
      scheduledAutoReveals.has(
        key,
      )
    ) {
      return;
    }

    scheduledAutoReveals.add(
      key,
    );

    if (
      status instanceof
        HTMLElement
    ) {
      status.textContent =
        "Call locked. Revealing…";
    }

    window.setTimeout(
      () => {
        void (
          async () => {
            try {
              const result =
                await options.client
                  .sendGameCommand(
                    options.game
                      .gameSessionId,

                    options.game
                      .stateVersion,

                    {
                      type:
                        "REVEAL",
                    },
                  );

              options.onGameSnapshot(
                result.snapshot,
              );
            } catch (error) {
              scheduledAutoReveals.delete(
                key,
              );

              const message =
                error instanceof
                  HostedPartyClientError
                  ? error.message
                  : error instanceof Error
                    ? error.message
                    : "Unable to reveal the card.";

              if (
                status instanceof
                  HTMLElement
              ) {
                status.textContent =
                  message;
              }

              const actions =
                options.panel
                  .querySelector(
                    ".hol-actions",
                  );

              if (
                actions instanceof
                  HTMLElement
              ) {
                const retry =
                  document.createElement(
                    "button",
                  );

                retry.type =
                  "button";

                retry.className =
                  "hol-action hol-primary";

                retry.textContent =
                  "RETRY REVEAL";

                retry.addEventListener(
                  "click",
                  () => {
                    retry.disabled =
                      true;

                    scheduledAutoReveals.delete(
                      key,
                    );

                    scheduleAutoReveal();
                  },
                  {
                    once: true,
                  },
                );

                actions.replaceChildren(
                  retry,
                );
              }
            }
          }
        )();
      },
      AUTO_REVEAL_DELAY_MS,
    );
  }

  scheduleAutoReveal();
  for (
    const button of
    buttons
  ) {
    button.addEventListener(
      "click",
      () => {
        const command =
          button.dataset
            .command;

        if (
          command ===
          "predict"
        ) {
          const choice =
            button.dataset
              .choice;

          if (choice) {
            void execute({
              type:
                "PREDICT",

              choice,
            });
          }

          return;
        }


        if (
          command ===
          "continue"
        ) {
          void execute({
            type:
              "CONTINUE",
          });

          return;
        }

        if (
          command ===
          "keep-going"
        ) {
          void execute({
            type:
              "KEEP_GOING",
          });

          return;
        }

        if (
          command ===
          "cash-out"
        ) {
          void execute({
            type:
              "CASH_OUT",
          });

          return;
        }

        if (
          command ===
          "risk"
        ) {
          const risk =
            button.dataset
              .risk;

          if (
            risk === "safe" ||
            risk === "double"
          ) {
            void execute({
              type:
                "SELECT_RISK",

              risk,
            });
          }

          return;
        }

        if (
          command ===
          "reward"
        ) {
          const target =
            button.dataset
              .target;

          const total =
            state.rewardDistribution
              ?.total;

          if (
            target &&
            typeof total ===
              "number"
          ) {
            void execute({
              type:
                "DISTRIBUTE_REWARD",

              allocations: {
                [target]:
                  total,
              },
            });
          }
        }
      },
    );
  }
}

export function renderOwnerHighOrLow(
  options:
    HighOrLowOwnerRenderOptions,
): void {
  const state =
    readHighOrLowState(
      options.game,
    );

  activateVisual(
    state.phase,
  );

  const activeName =
    playerName(
      options.party,
      state.activePlayerId,
    );

  const activeAvatar =
    playerAvatarAsset(
      playerAvatarId(
        options.party,
        state.activePlayerId,
      ),
    );

  const revealed =
    state.revealedCard;

  options.panel.innerHTML = `
    <div class="game-screen hol-screen owner-game-screen">
      <div class="game-topline">
        <p class="eyebrow">
          NIGHT ACTIVE · HIGH OR LOW
        </p>

        <span class="game-turn">
          PHASE ${
            phaseNumber(
              state.phase,
            )
          } / 6
        </span>
      </div>

      <div class="owner-active-player premium-owner-player">
        <img
          class="owner-player-avatar"
          src="${activeAvatar}"
          alt="${escapeHtml(activeName)}"
        />

        <div>
          <span class="meta">
            ACTIVE PLAYER
          </span>

          <strong>
            ${escapeHtml(activeName)}
          </strong>

          <span class="meta">
            ${phaseLabel(state.phase)}
          </span>
        </div>
      </div>

      ${finalBusProgress(state)}

      <section class="hol-table two-card owner-hol-table">
        ${
          cardHtml(
            state.currentCard,
            false,
            "CURRENT",
          )
        }

        ${
          revealed
            ? cardHtml(
                revealed,
                false,
                "REVEALED",
              )
            : cardHtml(
                null,
                (
                  state.step ===
                    "PREDICTION" ||
                  state.step ===
                    "LOCKED" ||
                  state.step ===
                    "FINAL_CARD"
                ),
                "NEXT CARD",
              )
        }
      </section>

      ${
        state.lockedPrediction
          ? `
            <div class="hol-locked-call">
              <span>
                LOCKED CALL
              </span>

              <strong>
                ${escapeHtml(
                  state.lockedPrediction
                    .toUpperCase(),
                )}
              </strong>
            </div>
          `
          : ""
      }

      ${outcomeHtml(state)}

      <div class="game-owner-meta">
        <span>
          State ${options.game.stateVersion}
        </span>

        <span>
          ${state.completedPredictions}
          calls
        </span>

        <span>
          ${state.deckRemaining}
          cards left
        </span>
      </div>
    </div>
  `;
}
